import * as yup from "yup";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField/TextField";
import { ApplicationRole, LoginRequest, UsersService } from "../../api";
import { useEffect, useState } from "react";
import useNavigateSearch from "../../services/useNavigateSearch";
import { AppButton, toast } from "../../components/ui";
import InputAdornment from "@mui/material/InputAdornment/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton/IconButton";
import { useLocation } from "react-router-dom";
import { LeadType } from "../../pages/Admin/LeadsManagement/shared/Constants";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../redux/actions/userActions";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { useGoogleLogin } from "@react-oauth/google";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import "./Login.scss";

const emptyData = {
  username: "",
  password: "",
};

const schema = yup.object().shape({
  username: yup.string().required("username"),
  password: yup.string().required("password"),
});
interface LoginParams {
  redirectTo?: string;
}

export default function Login(params: LoginParams) {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [redirectLink, setRedirectLink] = useState<string | null>();
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const user = useSelector((state: any) => state.user?.user);
  const navigate = useNavigateSearch();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    var redirectTo: string | null = params.redirectTo ?? null;
    if (!params.redirectTo) {
      const queryParams = new URLSearchParams(location.search);
      redirectTo = queryParams.get("redirectTo");
    }
    setRedirectLink(redirectTo);
  }, [location]);

  useEffect(() => {
    if (user != null) {
      navigateOnLoggedIn(new Set(user.roles))
    }
  }, [user]);

  // Google sign-in handler; its button is not rendered on the login page yet.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loginExternal = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      dispatch(startSpinner());
      UsersService.postApiUsersLoginExternal({
        accessToken: tokenResponse.access_token,
      })
        .then((data) => {
          processLoginResponse(data);
        })
        .finally(() => dispatch(stopSpinner()));
    },
  });

  const onSubmit = (value) => {
    dispatch(startSpinner());
    UsersService.postApiUsersLogin(value as LoginRequest)
      .then((data) => {
        processLoginResponse(data);
      })
      .finally(() => dispatch(stopSpinner()));
  };

  function processLoginResponse(data: any) {
    if (data.error) {
      toast.error(
        data.deactivated
          ? "This account has been deactivated. Contact an administrator."
          : "Wrong username or password"
      );
      return;
    }
    dispatch(setUser(data));
    navigateOnLoggedIn(new Set(data.roles));
  }

  const navigateOnLoggedIn = (rolesSet) => {
    switch (true) {
      case rolesSet.has(ApplicationRole.ADMIN):
      case rolesSet.has(ApplicationRole.STAFF): {
        navigate(
          !!redirectLink
            ? redirectLink.includes("lead")
              ? `${redirectLink}?leadType=${LeadType.Admin}&isEdit=false`
              : redirectLink
            : "/new-leads"
        );
        break;
      }
      case rolesSet.has(ApplicationRole.STAFF_ATTORNEY): {
        navigate(
          !!redirectLink
            ? redirectLink.includes("lead")
              ? `${redirectLink}?leadType=${LeadType.StaffAttorney}&isEdit=false`
              : redirectLink
            : "/approve-leads"
        );
        break;
      }
      case rolesSet.has(ApplicationRole.ATTORNEY): {
        navigate(redirectLink ?? "/attorney-leads");
        break;
      }
      case rolesSet.has(ApplicationRole.USER): {
        navigate(redirectLink ?? "/attorney-info");
        break;
      }
      default: {
        navigate(redirectLink ?? "/");
      }
    }
  }

  const handlePasswordReset = () => {
    if (!values.username) {
      toast.error("Please fill in email");
      return;
    }
    UsersService.postApiUsersRequestPasswordReset(values.username).then((error) => {
      if (!!error) {
        toast.error(error);
        return;
      }
      toast.success("Email was sent to you with link to reset password.");
    }
    ).catch(() =>
      toast.error(
        "Error requesting password reset. Please make sure you typed in right email."
      )
    );
  };

  const { values, handleBlur, handleChange, handleSubmit, setFieldValue } =
    useFormik({
      initialValues: emptyData,
      validationSchema: schema,
      onSubmit,
    });

  return (
    <div className="login-page">
      <aside className="login-page__brand">
        <div className="login-brand-lockup">
          <span className="login-brand-lockup__mark">GL</span>
          <span>
            Great <strong>Lawyers</strong>
          </span>
        </div>

        <div className="login-page__pitch">
          <h2>Every lead, from first call to final matter.</h2>
          <p>
            Intake, attorney review, referrals and Clio reporting in one place
            for the whole firm.
          </p>
          <ul className="login-page__features">
            <li>
              <CheckCircleOutlineIcon />
              Track leads through intake and attorney review
            </li>
            <li>
              <CheckCircleOutlineIcon />
              Share and refer matters to partner attorneys
            </li>
            <li>
              <CheckCircleOutlineIcon />
              Live Clio, Zoom and RingCentral reporting
            </li>
          </ul>
        </div>

        <p className="login-page__legal">
          &copy; {new Date().getFullYear()} Great Lawyers
        </p>
      </aside>

      <main className="login-page__form">
        <div className="login-card">
          <div className="login-card__mobile-brand">
            <span className="login-card__mobile-mark">GL</span>
            <span>
              Great <strong>Lawyers</strong>
            </span>
          </div>

          <h1 className="login-card__title">Welcome back</h1>
          <p className="login-card__subtitle">
            Sign in to continue to your dashboard.
          </p>

          <form className="login-form" onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              className="login-field"
              label="Email address"
              variant="outlined"
              name="username"
              type="email"
              autoComplete="email"
              value={values.username}
              onBlur={handleBlur}
              onChange={(e) => setFieldValue("username", e.target.value.trim())}
            />
            <TextField
              required
              fullWidth
              className="login-field"
              label="Password"
              variant="outlined"
              name="password"
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={values.password}
              onBlur={handleBlur}
              onChange={handleChange}
            />

            <AppButton
              variant="text"
              className="login-form__forgot"
              onClick={handlePasswordReset}
            >
              Forgot password?
            </AppButton>

            {/* <AppButton
              variant="contained"
              color="primary"
              className="submit-btn"
              onClick={() => loginExternal()}
            >
              Login with Google
            </AppButton> */}

            <AppButton
              variant="contained"
              color="primary"
              type="submit"
              className="login-form__submit"
            >
              Login
            </AppButton>
          </form>

          <p className="login-card__footer">
            Trouble signing in? Contact your administrator.
          </p>
        </div>
      </main>
    </div>
  );
}
